import {
  ApiUnauthorizedError,
  configureAdminBff,
  formatApiError,
  setAdminBffAccessToken,
} from '@avila-roffe/api-clients-admin-bff';
import { Toaster, toast } from '@avila-roffe/components-web';
import { I18nProvider } from '@avila-roffe/i18n';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import './styles.css';
import App from './app/app';
import { AuthProvider } from './auth/AuthProvider';
import { fetchBootstrapConfig } from './lib/fetchBootstrapConfig';
import { removeSplash } from './lib/splash';
import { useAuthStore } from './stores/authStore';
import { TenantBootstrapProvider } from './tenant/TenantBootstrapProvider';

function redirectToLogin() {
  window.location.replace('/login');
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiUnauthorizedError) {
        redirectToLogin();
        return;
      }
      const { title, description } = formatApiError(error);
      toast.error(title, { description });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiUnauthorizedError) return false;
        return failureCount < 1;
      },
    },
  },
});

async function bootstrap() {
  const config = await fetchBootstrapConfig();

  configureAdminBff({
    baseUrl: config?.bffUrl || (import.meta.env.VITE_ADMIN_BFF_URL as string | undefined) || '',
  });

  const { accessToken: storedToken, tokenExpiry: storedExpiry } = useAuthStore.getState();
  if (storedToken && storedExpiry !== null && storedExpiry > Date.now() + 30_000) {
    setAdminBffAccessToken(storedToken);
  }

  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  setTimeout(removeSplash, 4000);

  root.render(
    <StrictMode>
      <I18nProvider>
        <TenantBootstrapProvider>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <App />
              <Toaster position="bottom-right" richColors />
            </QueryClientProvider>
          </AuthProvider>
        </TenantBootstrapProvider>
      </I18nProvider>
    </StrictMode>,
  );
}

bootstrap().catch(console.error);
